import { FC, useCallback, useMemo } from 'react'

import { FiltersDefinition, FiltersState } from '@factorialco/f0-react'
import {
  EmployeeForSelectV2Fragment,
  EmployeeSelectorV2Document,
  EmployeeSelectorV2ItemsDocument,
  EmployeeSelectorV2ItemsQuery,
  EmployeeSelectorV2Query,
  EmployeeSelectorV2QueryVariables,
} from 'generated/resources/graphql'

import { EntitySelectorV2 } from 'modules/core/components/EntitySelectorV2'
import { MultiSelectState } from 'modules/core/components/EntitySelectorV2/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeFilters } from 'modules/employees/lib/filters'

import { EmployeeSelectorV2Props } from './types'

export const EmployeeSelectorV2: FC<EmployeeSelectorV2Props> = ({
  filters,
  forcedFilters,
  label,
  hint,
  messages,
  options,
  disabled,
  mandatory,
  ...props
}) => {
  const { employee, company } = useCurrentSession()

  const { filters: employeeDefaultFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()

  const mapExtraFiltersToVariables =
    'mapExtraFiltersToVariables' in props ? props.mapExtraFiltersToVariables : undefined

  const currentCompanyId = company.id
  const currentEmployeeId = employee?.id

  const extractConnection = useCallback(
    (data: EmployeeSelectorV2Query) => data.employees.employeeNamesConnection,
    []
  )

  const extractItems = useCallback(
    (data: EmployeeSelectorV2ItemsQuery) =>
      data.employees.employeesConnection.nodes.map((node) => node.employeeName),
    []
  )

  const mapItemTitle = useCallback(
    (item: EmployeeForSelectV2Fragment) =>
      resolveEmployeeDisplayName(
        {
          fullName: item.fullName,
          lastName: item.lastName,
          preferredName: item.preferredName,
        },
        options?.displayNameStrategy
      ),
    [options?.displayNameStrategy]
  )

  const mapItemAvatar = useCallback(
    (item: EmployeeForSelectV2Fragment) => {
      if (options?.hideAvatars) {
        return undefined
      }

      return {
        firstName: item.preferredName ?? item.firstName,
        lastName: item.lastName,
        src: buildUrl(item.access.avatar),
        type: 'person' as const,
      }
    },
    [options?.hideAvatars]
  )

  const finalFilters = useMemo(() => {
    let res: Partial<typeof employeeDefaultFilters> & FiltersDefinition = {}

    const isFilterEmployeeDefaultFilter = (
      filter: string
    ): filter is keyof typeof employeeDefaultFilters => {
      return filter in employeeDefaultFilters
    }

    if (!filters || filters.find((f) => typeof f === 'string' && f.startsWith('!'))) {
      res = employeeDefaultFilters
    }

    filters?.forEach((filter) => {
      if (typeof filter === 'string' && filter.startsWith('!')) {
        delete res[filter.slice(1)]
      } else if (typeof filter === 'string' && isFilterEmployeeDefaultFilter(filter)) {
        res = { ...res, [filter]: employeeDefaultFilters[filter] }
      } else if (typeof filter !== 'string') {
        res = { ...res, ...filter }
      }
    })

    return res
  }, [employeeDefaultFilters, filters])

  const mapFiltersToVariables = useCallback(
    ({
      forcedFilters: _forcedFilters,
      filters: _filters,
    }: {
      forcedFilters?: EmployeeSelectorV2Props['forcedFilters']
      filters?: FiltersState<typeof finalFilters>
    }): Omit<EmployeeSelectorV2QueryVariables, 'cursor' | 'perPage' | 'page'> => {
      let excludedIds = _forcedFilters?.excludeIds ? [..._forcedFilters.excludeIds] : undefined
      if (_forcedFilters?.excludeCurrentEmployee) {
        excludedIds = [...(excludedIds ?? []), currentEmployeeId]
      }

      let locationIds = _forcedFilters?.locationIds

      if (_filters?.workplace?.length) {
        locationIds = [...(locationIds ?? []), ..._filters.workplace]
      }

      let legalEntityIds = _forcedFilters?.legalEntityIds

      if (_filters?.legalEntity?.length) {
        legalEntityIds = [...(legalEntityIds ?? []), ..._filters.legalEntity]
      }

      let teamIds = _forcedFilters?.teamIds

      if (_filters?.team?.length) {
        teamIds = [...(teamIds ?? []), ..._filters.team]
      }

      let jobLevelIds: number[] | undefined = undefined
      let jobCatalogTreeNodeIds: string[] | undefined = undefined

      if (
        _filters?.jobRole &&
        Array.isArray(_filters.jobRole) &&
        _filters.jobRole.every((item) => typeof item === 'string')
      ) {
        jobLevelIds = !hasJobCatalogTreeFeature
          ? _filters?.jobRole?.map((id) => Number(id))
          : undefined
        jobCatalogTreeNodeIds = hasJobCatalogTreeFeature
          ? _filters?.jobRole?.map((id) => id.toString())
          : undefined
      }

      const filter = excludedIds ? { id: { notIn: excludedIds } } : {}

      const extraFilters = mapExtraFiltersToVariables?.({
        filters: _filters,
      })

      return {
        companyId: _forcedFilters?.companyId ?? currentCompanyId,
        legalEntityIds,
        teamIds,
        locationIds,
        referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
        referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
          ? jobCatalogTreeNodeIds
          : undefined,
        ids: _forcedFilters?.employeeIds,
        onlyActive: !_forcedFilters?.includeInactive,
        onlyManagers: _forcedFilters?.onlyManagers,
        onlyMyReportees: _forcedFilters?.onlyMyReportees,
        withActiveContract: _forcedFilters?.withActiveContract,
        reporteesOrLed: _forcedFilters?.reporteesOrLed,
        ...extraFilters,
        filter: {
          ...filter,
          ...extraFilters?.filter,
        },
      }
    },
    [currentCompanyId, currentEmployeeId, mapExtraFiltersToVariables, hasJobCatalogTreeFeature]
  )

  const finalLabel =
    label ?? i18n.tp('employees.select_employee', { count: props.multiple ? 2 : 1 })

  return (
    <EntitySelectorV2
      document={EmployeeSelectorV2Document}
      itemsDocument={EmployeeSelectorV2ItemsDocument}
      extractItems={extractItems}
      extractConnection={extractConnection}
      mapItemTitle={mapItemTitle}
      mapItemAvatar={mapItemAvatar}
      filters={finalFilters}
      forcedFilters={forcedFilters}
      mapFiltersToVariables={mapFiltersToVariables}
      options={options}
      disabled={disabled}
      label={finalLabel}
      hint={hint}
      messages={messages}
      mandatory={mandatory}
      {...(props.multiple === true
        ? {
            multiple: true,
            value: props.value,
            onChange: (state: MultiSelectState<FiltersState<FiltersDefinition> | undefined>) => {
              if (state.type === 'all' && !!forcedFilters?.includeInactive) {
                props.onChange({ ...state, includeInactive: true })
              } else {
                props.onChange(state)
              }
            },
          }
        : { value: props.value, onChange: props.onChange })}
    />
  )
}
